import type { CSSProperties, ReactNode } from 'react'

export type StatusTone = 'success' | 'warn' | 'danger' | 'info' | 'neutral' | 'gold'

interface StatusBadgeProps {
  tone: StatusTone
  children: ReactNode
  /** Renders with a small dot prefix (matches `.aufq-pill` style). */
  dot?: boolean
  size?: 'sm' | 'md'
  style?: CSSProperties
  className?: string
  title?: string
}

/**
 * A token-driven status pill. Replaces `<Tag color="green">` / hard-coded
 * background+color inline styles spread across the app.
 *
 * Colours come from CSS vars (`--aufq-success`, etc.) so they switch with
 * dark mode automatically.
 */
export function StatusBadge({
  tone,
  children,
  dot = true,
  size = 'md',
  style,
  className,
  title,
}: StatusBadgeProps) {
  const styles: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: size === 'sm' ? '2px 8px' : '3px 10px',
    borderRadius: 20,
    fontSize: size === 'sm' ? 10.5 : 11.5,
    fontWeight: 600,
    border: '1px solid transparent',
    background: `var(--aufq-${tone === 'neutral' ? 'bg-subtle' : `${tone}-soft`})`,
    color: tone === 'neutral' ? 'var(--aufq-ink-2)' : `var(--aufq-${tone})`,
    ...style,
  }

  return (
    <span className={className} style={styles} title={title}>
      {dot ? (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'currentColor',
          }}
        />
      ) : null}
      {children}
    </span>
  )
}

export default StatusBadge
